import type { OHLCData } from "../types";

type Pending = {
  resolve: (v: OHLCData[]) => void;
  reject: (e: Error) => void;
};

/**
 * Web Worker writes/reads 1m OHLC in IndexedDB, one row per UTC day.
 */
class OhlcIdbCacheManager {
  private worker: Worker | null = null;
  private loadPending = new Map<string, Pending>();
  private seq = 0;

  private initWorker(): void {
    if (this.worker || typeof Worker === "undefined") return;
    try {
      this.worker = new Worker(
        new URL("./ohlc-idb-cache.worker.ts", import.meta.url),
        { type: "module" }
      );
      this.worker.onmessage = (ev: MessageEvent) => {
        const { id, ok, bars, error } = ev.data as {
          id: string;
          ok: boolean;
          bars?: OHLCData[];
          error?: string;
        };

        if (typeof id === "string" && id.startsWith("put_")) {
          if (!ok) {
            console.warn("[OhlcIdbCache] putBars failed:", error);
          }
          return;
        }

        const p = this.loadPending.get(id);
        if (!p) return;
        this.loadPending.delete(id);
        if (ok) {
          p.resolve(bars ?? []);
        } else {
          p.reject(new Error(error || "Ohlc IDB worker error"));
        }
      };
      this.worker.onerror = (err) => {
        console.error("[OhlcIdbCache] worker error:", err);
        this.loadPending.forEach((p) =>
          p.reject(new Error("Ohlc IDB worker crashed"))
        );
        this.loadPending.clear();
        this.worker = null;
      };
    } catch (e) {
      console.warn("[OhlcIdbCache] Worker unavailable:", e);
      this.worker = null;
    }
  }

  private nextLoadId(): string {
    return `load_${++this.seq}_${Date.now()}`;
  }

  private nextPutId(): string {
    return `put_${++this.seq}_${Date.now()}`;
  }

  /** Fire-and-forget merge of 1m bars into per-day records. */
  enqueuePutBars(accountKey: string, bars: OHLCData[]): void {
    if (bars.length === 0) return;
    this.initWorker();
    if (!this.worker) return;
    const id = this.nextPutId();
    this.worker.postMessage({
      id,
      cmd: "putBars",
      payload: { accountKey, bars },
    });
  }

  async loadSymbolBars(
    accountKey: string,
    window?: { fromTs: number; toTs: number },
  ): Promise<OHLCData[]> {
    this.initWorker();
    if (!this.worker) {
      return [];
    }
    const id = this.nextLoadId();
    return new Promise<OHLCData[]>((resolve, reject) => {
      this.loadPending.set(id, { resolve, reject });
      this.worker!.postMessage({
        id,
        cmd: "loadSymbol",
        payload: {
          accountKey,
          fromTs: window?.fromTs,
          toTs: window?.toTs,
        },
      });
    });
  }
}

export const ohlcIdbDayCache = new OhlcIdbCacheManager();
